import React, { useState, useEffect } from 'react';
import { Flag, X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import Select from '../../../components/ui/Select';
import SearchSelect from '../../../components/ui/SearchSelect';
import Input from '../../../components/ui/Input';
// import Button from '../../../components/ui/Button';
import { useSelector, useDispatch } from 'react-redux';
import { selectSegments, selectSegmentsLoading } from '../../../store/segments';
import { getSegmentsRequest } from '../../../store/segments';
import {
  getUnsubscribeGroupsRequest,
  // selectUnsubscribeGroups,
} from '../../../store/unsubscribeGroups';

interface GoalAndExitProps {
  goal?: {
    type: string; // goal|no_goal
    condition: Object;
    timeDuration: {
      duration: number;
      unit: string;
      event: string;
    };
  };
  exitSurvey?: string;
  messages?: {
    subscriptionPreferences: string;
    countTowardsLimit: boolean;
    messageLimit?: {
      enabled: boolean;
      limit: number;
      period: string;
    };
    autoRetrySettings?: string;
    customRetryValue?: number;
    customRetryUnit?: string;
  };
  onGoalChange?: (goal: {
    type: string;
    condition: Object;
    timeDuration: {
      duration: number;
      unit: string;
      event: string;
    };
  }) => void;
  onExitSurveyChange?: (exitSurvey: string) => void;
  onMessagesChange?: (messages: {
    subscriptionPreferences: string;
    countTowardsLimit: boolean;
    messageLimit?: {
      enabled: boolean;
      limit: number;
      period: string;
    };
    autoRetrySettings?: string;
    customRetryValue?: number;
    customRetryUnit?: string;
  }) => void;
  onValidationChange?: (isValid: boolean) => void;
}

const GoalAndExit: React.FC<GoalAndExitProps> = ({
  goal,
  exitSurvey,
  messages,
  onGoalChange,
  onExitSurveyChange,
  onMessagesChange,
  onValidationChange,
}) => {
  const dispatch = useDispatch<any>();
  const segments = useSelector(selectSegments);
  const segmentsLoading = useSelector(selectSegmentsLoading);
  // const unsubscribeGroups = useSelector(selectUnsubscribeGroups);

  const goalType = goal?.type || 'goal';
  const condition =
    (goal?.condition as { operator?: string; value?: string }) || {};
  const timeDuration = goal?.timeDuration || {
    duration: 1,
    unit: 'week',
    event: 'being sent',
  };

  const [hasGoal, setHasGoal] = useState(goalType === 'goal');
  const [selectedOperator, setSelectedOperator] = useState(
    condition.operator || 'enters_segment'
  );
  const [selectedValue, setSelectedValue] = useState(condition.value || '');
  const [timeValue, setTimeValue] = useState(timeDuration.duration);
  const [timeUnit, setTimeUnit] = useState(timeDuration.unit);
  const [actionType, setActionType] = useState(timeDuration.event);
  const [selectedExitSurvey, setSelectedExitSurvey] = useState(
    exitSurvey || 'goal_or_trigger'
  );
  // const [subscriptionPreferences, setSubscriptionPreferences] = useState(
  //   messages?.subscriptionPreferences || 'subscribed'
  // );
  // const [countTowardsLimit, setCountTowardsLimit] = useState(
  //   messages?.countTowardsLimit || false
  // );
  // const [autoRetrySettings, setAutoRetrySettings] = useState(
  //   messages?.autoRetrySettings || 'do_not_retry_messages'
  // );
  // const [customRetryValue, setCustomRetryValue] = useState(
  //   messages?.customRetryValue ?? 1
  // );
  // const [customRetryUnit, setCustomRetryUnit] = useState(
  //   messages?.customRetryUnit ?? 'hour'
  // );

  // Fetch segments and unsubscribe groups on component mount
  useEffect(() => {
    dispatch(getSegmentsRequest({}));
    dispatch(getUnsubscribeGroupsRequest());
  }, [dispatch]);

  // Sync local state when goal prop changes
  useEffect(() => {
    if (goal) {
      const goalType = goal.type || 'goal';
      const condition =
        (goal.condition as { operator?: string; value?: string }) || {};
      const timeDuration = goal.timeDuration || {
        duration: 1,
        unit: 'week',
        event: 'being sent',
      };

      setHasGoal(goalType === 'goal');
      setSelectedOperator(condition.operator || 'enters_segment');
      setSelectedValue(condition.value || '');
      setTimeValue(timeDuration.duration);
      setTimeUnit(timeDuration.unit);
      setActionType(timeDuration.event);
    }
  }, [goal]);

  // Sync exitSurvey when prop changes
  useEffect(() => {
    if (exitSurvey !== undefined) {
      setSelectedExitSurvey(exitSurvey);
    }
  }, [exitSurvey]);

  // Sync messages when prop changes
  // useEffect(() => {
  //   if (messages) {
  //     setSubscriptionPreferences(
  //       messages.subscriptionPreferences || 'use_campaign_settings'
  //     );
  //     setCountTowardsLimit(messages.countTowardsLimit || false);
  //     setAutoRetrySettings(
  //       messages.autoRetrySettings || 'do_not_retry_messages'
  //     );
  //     setCustomRetryValue(messages.customRetryValue ?? 1);
  //     setCustomRetryUnit(messages.customRetryUnit ?? 'hour');
  //   }
  // }, [messages]);

  // Validate goal completeness
  const isGoalValid = (): boolean => {
    if (!hasGoal) {
      return true; // No goal is always valid
    }
    // Check if all required fields are filled
    return !!(
      selectedOperator &&
      selectedValue &&
      selectedValue.trim() !== '' &&
      timeValue > 0 &&
      timeUnit &&
      actionType
    );
  };

  // Notify parent about validation state
  useEffect(() => {
    if (onValidationChange) {
      const isValid = isGoalValid();
      onValidationChange(isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasGoal,
    selectedOperator,
    selectedValue,
    timeValue,
    timeUnit,
    actionType,
  ]);

  const operatorOptions = [
    { value: 'enters_segment', label: 'Enters segment' },
    { value: 'leaves_segment', label: 'Leaves segment' },
  ];

  // Map segments to options for the dropdown
  const segmentOptions = segmentsLoading
    ? [{ value: '', label: 'Loading segments...' }]
    : (segments || [])
        .filter((segment) => segment && segment.name && segment._id)
        .map((segment) => ({
          value: segment._id || '',
          label: segment.name || '',
          tag: segment.type,
        }));

  const timeUnitOptions = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
  ];

  const actionTypeOptions = [
    { value: 'sent', label: 'Being sent' },
    { value: 'opened', label: 'Opening' },
    { value: 'clicked', label: 'Clicking a tracked link in' },
  ];

  // const exitConditionOptions = [
  //   { value: 'goal', label: 'They achieve the goal' },
  //   {
  //     value: 'trigger',
  //     label: 'They stop matching the trigger segment or filters',
  //   },
  //   {
  //     value: 'goal_or_trigger',
  //     label:
  //       'They achieve the goal or they stop matching the trigger segment or filters',
  //   },
  //   {
  //     value: 'no_exit',
  //     label: "People don't exit early, they move through the entire workflow",
  //   },
  // ];

  // const subscriptionOptions = {
  //   ...(unsubscribeGroups.length > 0 && {
  //     Topics: unsubscribeGroups.map((group) => ({
  //       value: group._id || '',
  //       label: group.title,
  //     })),
  //   }),
  //   Global: [
  //     {
  //       value: 'subscribed',
  //       label: 'All subscribed people',
  //     },
  //     {
  //       value: 'all',
  //       label: 'All subscribed and unsubscribed',
  //     },
  //   ],
  // };

  const updateGoal = (
    updates: Partial<{
      type: string;
      condition: Object;
      timeDuration: { duration: number; unit: string; event: string };
    }>
  ) => {
    if (onGoalChange) {
      onGoalChange({
        type: updates.type ?? (hasGoal ? 'goal' : 'no_goal'),
        condition: updates.condition ?? {
          operator: selectedOperator,
          value: selectedValue,
        },
        timeDuration: updates.timeDuration ?? {
          duration: timeValue,
          unit: timeUnit,
          event: actionType,
        },
      });
    }
  };

  const handleGoalToggle = (value: boolean) => {
    setHasGoal(value);

    // If switching to "no goal" and exit survey is goal or goal_or_trigger, change to no_exit
    if (
      !value &&
      (selectedExitSurvey === 'goal' ||
        selectedExitSurvey === 'goal_or_trigger')
    ) {
      setSelectedExitSurvey('no_exit');
      if (onExitSurveyChange) {
        onExitSurveyChange('no_exit');
      }
    }

    updateGoal({
      type: value ? 'goal' : 'no_goal',
    });
  };

  // const handleExitSurveyChange = (value: string) => {
  //   setSelectedExitSurvey(value);
  //   if (onExitSurveyChange) {
  //     onExitSurveyChange(value);
  //   }
  // };

  // const handleMessagesChange = (
  //   updates: Partial<{
  //     subscriptionPreferences: string;
  //     countTowardsLimit: boolean;
  //     messageLimit?: {
  //       enabled: boolean;
  //       limit: number;
  //       period: string;
  //     };
  //     autoRetrySettings?: string;
  //     customRetryValue?: number;
  //     customRetryUnit?: string;
  //   }>
  // ) => {
  //   if (onMessagesChange) {
  //     onMessagesChange({
  //       subscriptionPreferences:
  //         updates.subscriptionPreferences ?? subscriptionPreferences,
  //       countTowardsLimit: updates.countTowardsLimit ?? countTowardsLimit,
  //       messageLimit: updates.messageLimit ?? messages?.messageLimit,
  //       autoRetrySettings: updates.autoRetrySettings ?? autoRetrySettings,
  //       customRetryValue: updates.customRetryValue ?? customRetryValue,
  //       customRetryUnit: updates.customRetryUnit ?? customRetryUnit,
  //     });
  //   }
  // };

  return (
    <div className="flex h-[calc(85vh-60px)]">
      {/* Left Column - Goal and Exit Configuration */}
      <div className="flex-1 overflow-y-auto bg-background p-6">
        <div className="max-w-4xl mx-auto bg-card rounded-lg border border-border">
          {/* Set a goal and conversion criteria */}
          <div className="p-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Set a goal and conversion criteria
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Goals and conversions help you measure how successful your
              campaign is in getting your audience to do something.
            </p>

            {/* Goal Selection Buttons */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => handleGoalToggle(true)}
                className={cn(
                  'flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors flex-1 text-sm',
                  hasGoal
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                <Flag className="w-4 h-4" />
                <span className="font-medium">Set goal</span>
              </button>
              <button
                onClick={() => handleGoalToggle(false)}
                className={cn(
                  'flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors flex-1 text-sm',
                  !hasGoal
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                <div
                  className={cn(
                    'w-4 h-4 rounded-full border-2 flex items-center justify-center',
                    !hasGoal ? 'border-primary' : 'border-muted-foreground'
                  )}
                >
                  <X
                    className={cn(
                      'w-2.5 h-2.5',
                      !hasGoal ? 'text-primary' : 'text-muted-foreground'
                    )}
                  />
                </div>
                <span className="font-medium">No goal</span>
              </button>
            </div>

            {/* Goal Definition */}
            {hasGoal && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <h3 className="font-semibold text-foreground text-sm">
                    Conversion criteria
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    People meet the conversion criteria when they:
                  </p>

                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Select
                        value={selectedOperator}
                        onChange={(e) => {
                          setSelectedOperator(e.target.value);
                          updateGoal({
                            condition: {
                              operator: e.target.value,
                              value: selectedValue,
                            },
                          });
                        }}
                        options={operatorOptions}
                        className="h-10 text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <SearchSelect
                        placeholder="Select segment"
                        searchPlaceholder="Find or specify a segment..."
                        value={selectedValue}
                        onChange={(value) => {
                          setSelectedValue(value);
                          updateGoal({
                            condition: {
                              operator: selectedOperator,
                              value: value,
                            },
                          });
                        }}
                        options={segmentOptions}
                        className="h-10 text-sm w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Conversion Window */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-foreground text-sm">
                    Conversion window
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    A conversion is counted when a person:
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-foreground">
                        Achieves the goal within
                      </span>
                      <div className="w-14">
                        <Input
                          type="number"
                          value={timeValue}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 1;
                            setTimeValue(value);
                            updateGoal({
                              timeDuration: {
                                duration: value,
                                unit: timeUnit,
                                event: actionType,
                              },
                            });
                          }}
                          className="h-8 text-center text-xs"
                          min="1"
                        />
                      </div>
                      <div className="w-24">
                        <Select
                          options={timeUnitOptions}
                          value={timeUnit}
                          onChange={(e) => {
                            setTimeUnit(e.target.value);
                            updateGoal({
                              timeDuration: {
                                duration: timeValue,
                                unit: e.target.value,
                                event: actionType,
                              },
                            });
                          }}
                          className="h-8 text-xs"
                        />
                      </div>
                      <span className="text-foreground">of</span>
                      <div className="w-32">
                        <Select
                          options={actionTypeOptions}
                          value={actionType}
                          onChange={(e) => {
                            setActionType(e.target.value);
                            updateGoal({
                              timeDuration: {
                                duration: timeValue,
                                unit: timeUnit,
                                event: e.target.value,
                              },
                            });
                          }}
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      any delivery from this campaign.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* <hr className="border-border" /> */}
          {/* Messages Section */}
          {/* <div className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-6 h-6 bg-success-background rounded-lg flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-success" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Messages
                </h2>
                <p className="text-sm text-muted-foreground">
                  Set the default settings for messages in this campaign. You
                  can override them for each message.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-foreground text-sm">
                    Subscription preferences
                  </h3>
                  <button className="text-muted-foreground hover:text-foreground">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>
                <Select
                  options={subscriptionOptions}
                  value={subscriptionPreferences}
                  onChange={(e) => {
                    setSubscriptionPreferences(e.target.value);
                    handleMessagesChange({
                      subscriptionPreferences: e.target.value,
                    });
                  }}
                  className="h-10 text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Choose which people will receive messages from the campaign.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-foreground text-sm">
                  Count towards message limit
                </h3>
                <p className="text-xs text-muted-foreground">
                  Control how many messages a person receives in a given time
                  period with a message limit.
                </p>
                <Button
                  variant="outline"
                  size="small"
                  className="w-full flex items-center justify-center space-x-1 text-xs h-8"
                >
                  <span>Set up message limit</span>
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-foreground text-sm">
                  Auto-retry messages
                </h3>
                <Select
                  options={[
                    {
                      value: 'do_not_retry_messages',
                      label: "Don't retry messages in this campaign",
                    },
                    {
                      value: 'custom_window',
                      label: 'Set a custom window',
                    },
                  ]}
                  value={autoRetrySettings}
                  onChange={(e) => {
                    setAutoRetrySettings(e.target.value);
                    if (e.target.value === 'custom_window') {
                      setCustomRetryValue(1);
                      setCustomRetryUnit('hour');
                    }
                    handleMessagesChange({
                      autoRetrySettings: e.target.value,
                      customRetryValue:
                        e.target.value === 'custom_window' ? 1 : undefined,
                      customRetryUnit:
                        e.target.value === 'custom_window' ? 'hour' : undefined,
                    });
                  }}
                  className="h-10 text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  When messages hit the limit, you can{' '}
                  <span className="underline">auto retry messages</span> for up
                  to 48 hours.
                </p>

                {autoRetrySettings === 'custom_window' && (
                  <div className="space-y-2 mt-3 p-3 bg-muted/50 rounded-lg border border-border">
                    <p className="text-sm text-muted-foreground">
                      If the limit is reached, retry messages for up to (max 48
                      hours):
                    </p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={customRetryValue}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (customRetryUnit === 'minutes' && value > 2880) {
                            setCustomRetryValue(2880);
                            handleMessagesChange({
                              customRetryValue: 2880,
                            });
                          } else if (customRetryUnit === 'hour' && value > 48) {
                            setCustomRetryValue(48);
                            handleMessagesChange({
                              customRetryValue: 48,
                            });
                          } else {
                            setCustomRetryValue(value);
                            handleMessagesChange({
                              customRetryValue: value,
                            });
                          }
                        }}
                        className="w-20 px-3 py-2 border border-border rounded-md text-sm bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                        min="1"
                        max={customRetryUnit === 'minutes' ? '2880' : '48'}
                      />
                      <Select
                        value={customRetryUnit}
                        onChange={(e) => {
                          setCustomRetryUnit(e.target.value);
                          const maxValue =
                            e.target.value === 'minutes' ? 2880 : 48;
                          const newValue =
                            customRetryValue > maxValue
                              ? maxValue
                              : customRetryValue;
                          setCustomRetryValue(newValue);
                          handleMessagesChange({
                            customRetryUnit: e.target.value,
                            customRetryValue: newValue,
                          });
                        }}
                        options={[
                          { value: 'minutes', label: 'minutes' },
                          { value: 'hour', label: 'hour' },
                        ]}
                        className="w-24 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div> */}
          {/* <hr className="border-border" /> */}
          {/* People exit the campaign early when */}
          {/* <div className="p-6">
            <h2 className="text-xl font-semibold text-foreground mb-6">
              People exit the campaign early when
            </h2>

            <div className="space-y-3">
              {exitConditionOptions.map((option) => {
                const isDisabled =
                  !hasGoal &&
                  (option.value === 'goal' ||
                    option.value === 'goal_or_trigger');
                return (
                  <label
                    key={option.value}
                    className={cn(
                      'flex items-center gap-2 rounded-lg transition-colors',
                      isDisabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer hover:bg-muted/50'
                    )}
                  >
                    <input
                      type="radio"
                      name="exitSurvey"
                      value={option.value}
                      checked={selectedExitSurvey === option.value}
                      onChange={(e) => handleExitSurveyChange(e.target.value)}
                      disabled={isDisabled}
                      className={cn(
                        'w-4 h-4 text-primary bg-muted border-border focus:outline-0 focus:ring-0',
                        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                      )}
                    />
                    <span
                      className={cn(
                        'text-sm',
                        isDisabled ? 'text-muted-foreground' : 'text-foreground'
                      )}
                    >
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default GoalAndExit;
